import type React from 'react';
import { useMemo, useState } from 'react';

import Loader from '@vlabs/shared/legacy-components/loader/Loader';

import {
  DEFAULT_STREAMS_PAGE_SIZE,
  SourceFastFilters,
  SourceFilterMenuButton,
  SourceFiltersMenu,
  SourceFiltersProvider,
  SourceGroupSearchInput,
  SourceGroupSortDropdown,
  useGroupStreamsCounts,
} from '@vlabs/monokit/entities/sources';
import { CreateStreamButton } from '@vlabs/monokit/features/create-stream';
import { StartStopGroupStreamsButtons } from '@vlabs/monokit/features/start-stop-group-streams';
import {
  buildStreamsColumns,
  GenericStreamListTable,
} from '@vlabs/monokit/widgets/source-group-list-viewer';
import { useSourceGroupListViewerTranslation } from '@vlabs/monokit/widgets/source-group-list-viewer/i18n';

import { useSourceGroupStreamsTranslation } from './i18n';
import st from './SourceGroupStreams.module.sass';
import type { SourceGroupStreamsProps } from './types';

function SourceGroupStreams({
  group,
  hiddenStreamIds = undefined,
  onEnqueueDelete = undefined,
  onEnqueueTransfer = undefined,
}: SourceGroupStreamsProps): React.JSX.Element {
  const { t } = useSourceGroupStreamsTranslation();
  const { t: tColumns } = useSourceGroupListViewerTranslation();
  const { total, inProgress, isLoading } = useGroupStreamsCounts(group.group_id);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const isEmpty = !isLoading && total === 0;

  const columns = useMemo(
    () => buildStreamsColumns({
      t: tColumns,
      group,
      onEnqueueDelete,
    }),
    [group, onEnqueueDelete, tColumns],
  );

  if (isLoading) {
    return <Loader />;
  }

  let streamsContent: React.JSX.Element;
  if (isEmpty) {
    streamsContent = (
      <div className={st.Empty}>
        <p className={st.EmptyText}>{t('empty')}</p>
        <CreateStreamButton
          groupId={group.group_id}
          kind="primary"
          size="medium"
          variant="outlined"
        />
      </div>
    );
  } else {
    streamsContent = (
      <div className={st.Content}>
        <SourceFastFilters />
        <GenericStreamListTable
          className={st.StreamsTable}
          columns={columns}
          groupId={group.group_id}
          hiddenStreamIds={hiddenStreamIds}
          onEnqueueDelete={onEnqueueDelete}
          onEnqueueTransfer={onEnqueueTransfer}
        />
      </div>
    );
  }

  return (
    <SourceFiltersProvider
      initialData={{
        group_id: group.group_id,
        page: 1,
        page_size: DEFAULT_STREAMS_PAGE_SIZE,
        order: 'desc',
      }}
    >
      <div className={st.Root}>
        <div className={st.Toolbar}>
          <div className={st.ToolbarLeft}>
            <h2 className={st.Title}>{t('title')}</h2>
            <span className={st.Counts}>
              {t('stream_count', { count: total })}
              <span className={st.InProgress}>
                {t('in_work_count', { count: inProgress })}
              </span>
            </span>
            {!isEmpty && <SourceGroupSearchInput />}
          </div>

          {!isEmpty && (
            <div className={st.ToolbarActions}>
              <SourceGroupSortDropdown />
              <StartStopGroupStreamsButtons groupId={group.group_id} />
              <CreateStreamButton groupId={group.group_id} />
              <SourceFilterMenuButton
                data-testid="source-group-streams-filter"
                isOpen={isFiltersOpen}
                onToggle={() => setIsFiltersOpen((prev) => !prev)}
              />
            </div>
          )}
        </div>

        {streamsContent}

        {!isEmpty && !!isFiltersOpen && (
          <div className={st.FilterMenuWrap}>
            <SourceFiltersMenu onClose={() => setIsFiltersOpen(false)} />
          </div>
        )}
      </div>
    </SourceFiltersProvider>
  );
}

export { SourceGroupStreams };
